
import {group, listify, objectify, unique} from "radash";

export const allStateFilters = [
    "failed",
    "started",
    "completed",
    "timed-out"
]

const taskGroupFunc = task => task.key.split(".")[0]


const taskTablePreReducer = (state, action) => {

    switch (action.name) {
        case "init":
            return {
                tasks: action.tasks,
                expandedRowPrefixes: {},
                refreshCounter: 0,
                tailLogsCounter: 0,
                stateFilters: [],
                taskKeyPrefixFilters: [],
                isTailLogs: false,
                isAutoRefresh: false,
                rows: null,
                lastLogsStoppedTasksPerKey: {},
                pipelineInstance: action.pipelineInstance,
                isShowAllTasks: false,
                c: 0
            }
        case "refreshTasks":

            const lastLogsStoppedTasksPerKey = objectify(
                    action.tasks.filter(task => {
                        switch (task.state) {
                            case "completed":
                                return true
                            case "failed":
                                return true
                            case "killed":
                                return true
                            default:
                                return false
                        }
                    }),
                    t => t.key,
                    t => t.log
                )
            return {
                ...state,
                tasks: action.tasks,
                rows: null,
                lastLogsStoppedTasksPerKey: {
                    ...state.lastLogsStoppedTasksPerKey,
                    //lastLogsStoppedTasksPerKey
                }
            }
        case "addStateFilter": {
            return {
                ...state,
                stateFilters: [...state.stateFilters, action.state],
                rows: null
            }
        }
        case "removeStateFilter":
            return {
                ...state,
                stateFilters: state.stateFilters.filter(f => f !== action.state),
                rows: null
            }
        case "addTaskKeyPrefixFilter":
            return {
                ...state,
                taskKeyPrefixFilters: unique([...state.taskKeyPrefixFilters, action.keyPrefix]),
                rows: null
            }
        case "removeTaskKeyPrefixFilter":
            return {
                ...state,
                taskKeyPrefixFilters: state.taskKeyPrefixFilters.filter(f => f !== action.keyPrefix),
                rows: null
            }
        case "addPrefixAndStateFilter":
            return {
                ...state,
                taskKeyPrefixFilters: unique([...state.taskKeyPrefixFilters, action.keyPrefix]),
                stateFilters: unique([...state.stateFilters, action.state]),
                expandedRowPrefixes: {
                    ...state.expandedRowPrefixes, [action.keyPrefix]: true
                },
                rows: null
            }
        case "toggleExpandRow":
            return {
                ...state,
                expandedRowPrefixes: {
                    ...state.expandedRowPrefixes,
                    [action.row.groupPrefix]: !action.row.isExpanded
                },
                rows: null
            }
        case "removeAllFilters":
            return {
                ...state,
                taskKeyPrefixFilters: [],
                stateFilters: [],
                rows: null
            }
        case "toggleTailLogs":
            return {
                ...state,
                isTailLogs: action.newValue,
                isAutoRefresh: action.newValue ? true : state.isTailLogs,
                rows: null
            }
        case "toggleAutoRefresh":
            return {
                ...state,
                isAutoRefresh: action.newValue,
                isTailLogs: action.newValue === false ? false: state.isTailLogs,
                rows: null
            }
        case "toggleShowAllTasks": {
            return {
                ...state,
                isShowAllTasks: action.newValue,
                rows: null
            }
        }
        default:
            throw Error(`unknown action: ${action.name}`)
    }
}

export const taskTableReducer = (state, action) => {

    state = taskTablePreReducer(state, action)

    state.c += 1

    if(state.rows !== null) {
        return state
    }

    function isTaskIncludedByState(task) {
        if (state.stateFilters.length === 0) {
            return true
        }

        function acceptTaskState(taskState, stateFilter) {
            switch (stateFilter) {
                case "completed":
                case "failed":
                case "timed-out":
                    return taskState === stateFilter
                case "started":
                    return taskState.indexOf("started") !== -1
                default:
                    throw Error(`unknown filter '${stateFilter}'`)
            }
        }

        return !!state.stateFilters.find(f => acceptTaskState(task.state, f))
    }

    function isTaskIncludedByPrefix(task) {
        if (state.taskKeyPrefixFilters.length === 0) {
            return true
        }
        return !!state.taskKeyPrefixFilters.find(f => task.key.startsWith(f))
    }

    function* buildRows() {

        const filteredTasks = state.tasks.filter(t => {

            if(! state.isShowAllTasks) {
                if(t.parent_array_key) {
                    return false
                }

                if(state.pipelineInstance.preRunFilters.find(k => t.key === k)) {
                    return false
                }
            }

            if (state.taskKeyPrefixFilters.length === 0) {
                return isTaskIncludedByState(t)
            }

            return isTaskIncludedByPrefix(t) && isTaskIncludedByState(t)
        })

        for (let [groupPrefix, tasksInGroup] of Object.entries(group(filteredTasks, taskGroupFunc))) {

            const isSingleTaskGroup = tasksInGroup.length === 1

            if (isSingleTaskGroup) {

                yield {
                    rowId: tasksInGroup[0].key,
                    isSingleTaskGroup,
                    task: tasksInGroup[0]
                }

                continue
            }

            if (!state.expandedRowPrefixes[groupPrefix]) {
                const summaryDict = group(tasksInGroup, t => t.state)
                const statesWithCounts = listify(summaryDict, (k, tasksInState) => ({
                    state: k,
                    tasks: tasksInState,
                    taskCountInState: tasksInState.length
                }))

                yield {
                    rowId: groupPrefix,
                    isExpanded: false,
                    groupPrefix,
                    statesWithCounts,
                    taskCount: tasksInGroup.length
                }

                continue
            }

            const [firstTask, ...restOfTasks] = tasksInGroup

            yield {
                isFirst: true,
                isExpanded: true,
                rowId: firstTask.key,
                groupPrefix,
                rowSpan: tasksInGroup.length,
                task: firstTask,
                keySuffix: firstTask.key.substring(groupPrefix.length + 1)
            }

            for (let task of restOfTasks) {
                yield {
                    rowId: task.key,
                    isExpanded: true,
                    groupPrefix,
                    keySuffix: task.key.substring(groupPrefix.length + 1),
                    task
                }
            }
        }
    }

    return {
        ...state,
        rows: [...buildRows()]
    }
}


export const addTaskKeyPrefixFilter = keyPrefix =>
    ({name: "addTaskKeyPrefixFilter", keyPrefix})

export const addPrefixAndStateFilter = (keyPrefix, state) =>
    ({name: "addPrefixAndStateFilter", keyPrefix, state})

export const toggleExpandRow = row =>
    ({name: "toggleExpandRow", row})

export const removeAllFilters = () =>
    ({name: "removeAllFilters"})

export const addStateFilter = state =>
    ({name: "addStateFilter", state})

export const removeStateFilter = state =>
    ({name: "removeStateFilter", state})

export const removeTaskKeyPrefixFilter = keyPrefix =>
    ({name: "removeTaskKeyPrefixFilter", keyPrefix})

export const setToggleTailLogs = newValue =>
    ({name: "toggleTailLogs", newValue})

export const setToggleAutoRefresh = newValue =>
    ({name: "toggleAutoRefresh", newValue})

export const setToggleShowAllTasks = newValue =>
    ({name: "toggleShowAllTasks", newValue})